/**
 * Componente para renderizar mensagens com lista de opções interativa (option_list)
 * Exibe o texto da pergunta, título opcional, e lista de opções com descrições
 * As opções são visuais apenas (não clicáveis no histórico)
 * Suporta variante WhatsApp com estilo integrado
 */
import { cn } from "@/lib/utils";
import { ChevronRight, List } from "lucide-react";

interface OptionListMessageBubbleProps {
  content: string;
  title?: string;
  options: Array<{ id: string; label: string; description?: string }>;
  direction: 'inbound' | 'outbound';
  variant?: 'default' | 'whatsapp';
  onOptionClick?: (optionId: string, optionLabel: string) => void;
}

export function OptionListMessageBubble({ 
  content, 
  title, 
  options, 
  direction,
  variant = 'default',
  onOptionClick
}: OptionListMessageBubbleProps) {
  
  // Variante WhatsApp: tudo integrado no mesmo bubble
  if (variant === 'whatsapp') {
    return (
      <div 
        className="shadow-sm overflow-hidden"
        style={{
          backgroundColor: 'hsl(var(--chat-bubble-inbound))',
          borderRadius: '16px',
          borderBottomLeftRadius: direction === 'inbound' ? '4px' : '16px',
          borderBottomRightRadius: direction === 'outbound' ? '4px' : '16px',
        }}
      >
        {/* Texto da pergunta */}
        {content && (
          <div className="px-4 py-2.5">
            <p className="whitespace-pre-wrap text-sm" style={{ color: 'hsl(var(--chat-text-primary))' }}>
              {content}
            </p>
          </div>
        )}
        
        {/* Divisória */}
        <div style={{ borderTop: '1px solid hsl(var(--chat-border))' }} />
        
        {/* Botão de lista estilo WhatsApp */}
        <button
          className="w-full px-4 py-3 text-sm text-center flex items-center justify-center gap-2 hover:opacity-80 transition-colors"
          style={{ color: '#00a884' }}
          onClick={() => {
            // Se há opções, mostra primeira como seleção default
            if (options.length > 0 && onOptionClick) {
              onOptionClick(options[0].id, options[0].label);
            }
          }}
        >
          <List className="w-4 h-4" />
          {title || 'Ver opções'}
        </button>
        
        {/* Opções expandidas (sempre visíveis no histórico) */}
        {options && options.length > 0 && (
          <div style={{ borderTop: '1px solid hsl(var(--chat-border))' }}>
            {options.map((option, idx) => (
              <button
                key={option.id}
                onClick={() => onOptionClick?.(option.id, option.label)}
                className="w-full px-4 py-3 text-left hover:opacity-80 transition-colors"
                style={{
                  borderTop: idx > 0 ? '1px solid hsl(var(--chat-border))' : 'none'
                }}
              >
                <div className="text-sm" style={{ color: '#00a884' }}>
                  {option.label}
                </div>
                {option.description && (
                  <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--chat-text-secondary))' }}>
                    {option.description}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  // Variante padrão: container único com texto + opções na mesma largura
  return (
    <div 
      className="rounded-lg overflow-hidden max-w-[280px]"
      style={{ backgroundColor: 'hsl(var(--chat-bubble-inbound))' }}
    >
      {/* Texto da pergunta - dentro do container unificado */}
      {content && (
        <div className="px-4 py-2.5">
          <p 
            className="text-sm whitespace-pre-wrap break-words"
            style={{ color: 'hsl(var(--chat-text-primary))' }}
          >
            {content}
          </p>
        </div>
      )}
      
      {/* Título da lista */}
      {title && (
        <div 
          className="px-4 py-2 text-sm font-semibold"
          style={{ 
            color: 'hsl(var(--chat-text-primary))',
            borderTop: content ? '1px solid hsl(var(--chat-border))' : 'none',
            borderBottom: '1px solid hsl(var(--chat-border))'
          }}
        >
          {title}
        </div>
      )}
      
      {/* Opções - mesma largura do texto */}
      {options && options.length > 0 && (
        <div style={{ borderTop: (!title && content) ? '1px solid hsl(var(--chat-border))' : 'none' }}>
          {options.map((option, index) => (
            <button
              key={option.id}
              onClick={() => onOptionClick?.(option.id, option.label)}
              className="w-full px-4 py-3 text-left transition-colors flex items-start gap-3 hover:opacity-80"
              style={{
                borderTop: (index !== 0 || title) ? '1px solid hsl(var(--chat-border))' : 'none'
              }}
            >
              <div className="flex-1">
                <div 
                  className="text-sm break-words"
                  style={{ color: '#00a884' }}
                >
                  {option.label}
                </div>
                {option.description && (
                  <div 
                    className="text-xs mt-0.5"
                    style={{ color: 'hsl(var(--chat-text-secondary))' }}
                  >
                    {option.description}
                  </div>
                )}
              </div>
              <ChevronRight 
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                style={{ color: 'hsl(var(--chat-text-secondary))' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
